import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
import {
    PrinterCheckStatusPayload,
    PrinterSettings,
} from 'hooks/useHardwareAgent/Devices/types'
import { useCallback } from 'react'
import toast from 'utilities/toast/toast'
import createHWAgentErrorMessage from 'utilities/createHWAgentErrorMessage'
import { usePrinterHubContext } from 'hooks/usePOSHubContext/usePOSHubContext'
import useDeviceHardwareAgentCaller from './useDeviceHardwareAgentCaller'
import { PrinterContract, PrinterMode, PrinterType } from 'models'
import { useConfirmDialog } from 'store/actions/ui.action'
import { logHWError } from 'utilities/logError'
import { NetworkPrinterWritterType } from 'models/NetworkPrinterWritterType'
const convertPrinterContractToPrinterConfig = (
    value: PrinterContract,
): IPrinterConfig | undefined => {
    if (value.printerType === PrinterType.Network) {
        return {
            printerType: value.printerType,
            name: value.name || '',
            ipAddress: value.ip || '',
            port: parseInt(value?.port || '9100'),
            isImage: value.printerMode === PrinterMode.Image,
            cutting: value.cutting,
            printImageDelay: value.printImageDelay || 100,
            networkPrinterWritterType: value.networkPrinterWritterType,
            printCoolDownDelay: value.printCoolDownDelay || 0,
        }
    } else if (value.printerType === PrinterType.Serial) {
        return {
            printerType: value.printerType,
            name: value.name || '',
            com: value.com || '',
            baudRate: value.baudRate || 115200,
            isImage: value.printerMode === PrinterMode.Image,
            cutting: value.cutting,
            printImageDelay: value.printImageDelay || 100,
            printCoolDownDelay: value.printCoolDownDelay || 0,
        }
    } else if (value.printerType === PrinterType.Usb) {
        return {
            printerType: value.printerType,
            name: value.name || '',
            pid: value.pid,
            vid: value.vid,
            isImage: value.printerMode === PrinterMode.Image,
            cutting: value.cutting,
            printImageDelay: value.printImageDelay || 100,
            printCoolDownDelay: value.printCoolDownDelay || 0,
        }
    }
    return undefined
}
export interface IPrinterConfig {
    name: string
    ipAddress?: string
    port?: number
    uniqueID?: string
    com?: string
    baudRate?: number
    data?: string
    pid?: number | null
    vid?: number | null
    isImage?: boolean
    cutting?: boolean
    printImageDelay?: number
    printerType: string
    printCoolDownDelay?: number | null
    networkPrinterWritterType?: NetworkPrinterWritterType
}
export interface IVersionModel {
    version: string
    informationalVersion: string
}
export interface UsbPrinter {
    deviceId: string
    deviceName: string
    manufacture: string
    productId: number
    vendorId: number
}

const displayErrorToast = (err: any, title: string, isOnline: boolean) => {
    if (!isOnline) {
        toast.error('Printer HwAgent is offline')
        // print error to console to allow to see error in PostHog
        console.log('Printer HwAgent is offline', err)
    } else {
        toast.error(createHWAgentErrorMessage(err, title))
        throw err
    }
}

/**
 * the function in this hook will use the printer hardware agent that config to current terminal
 */
const useCurrentTerminalDevicesHwAgentCaller = () => {
    const { isOnline: isOnlineHWagent } = usePrinterHubContext()
    const {
        printingHardwareAgentUri,
        defaultPrinter,
        currentPosTerminal,
    } = useDefaultDevice()
    const { showErrorMessageDialog } = useConfirmDialog()

    const {
        handlePrint,
        openDrawerRequest,
        checkStatusRequest,
        getPrinterStatusRequest,
        browseUsbDevicesRequest,
    } = useDeviceHardwareAgentCaller(printingHardwareAgentUri)

    const checkStatus = useCallback(
        async (requestId: string, data: PrinterSettings[]) => {
            const payload: PrinterCheckStatusPayload = {
                requestId,
                printers: data,
            }
            try {
                await checkStatusRequest(payload)
            } catch (error) {
                displayErrorToast(
                    error,
                    'Cannot check printer status',
                    isOnlineHWagent,
                )
            }
        },
        [checkStatusRequest, isOnlineHWagent],
    )

    const onPrint = useCallback(
        async (data: string, tag?: string, printer?: PrinterContract) => {
            try {
                const printerConfig = convertPrinterContractToPrinterConfig(
                    printer ??
                        defaultPrinter ?? { printerType: PrinterType.Unknown },
                )
                if (printerConfig) {
                    await handlePrint(printerConfig, data, tag)
                } else {
                    showErrorMessageDialog({
                        message: 'Printer is not setup.',
                        arg: {},
                    })
                }
                toast.success('print successfully')
            } catch (error) {
                displayErrorToast(error, 'Disconnect printer', isOnlineHWagent)
            }
        },
        [defaultPrinter, handlePrint, isOnlineHWagent, showErrorMessageDialog],
    )

    const openDrawer = useCallback(
        async (callerName: string, printer?: PrinterContract) => {
            const printerConfig = convertPrinterContractToPrinterConfig(
                printer ??
                    defaultPrinter ?? { printerType: PrinterType.Unknown },
            )

            // Ignore send openDrawer request if there is no cash drawer used
            if (!currentPosTerminal?.isUseCashDrawer) return

            try {
                if (printerConfig) {
                    await openDrawerRequest(printerConfig)
                    toast.success('open drawer successfully')
                }
            } catch (error) {
                if (!isOnlineHWagent) toast.error('Printer HwAgent is offline')
                else logHWError(callerName, 'Open drawer', error)
            }
        },
        [
            currentPosTerminal,
            defaultPrinter,
            isOnlineHWagent,
            openDrawerRequest,
        ],
    )

    const browseUsbDevices = useCallback(async () => {
        try {
            return await browseUsbDevicesRequest()
        } catch (error) {
            displayErrorToast(
                error,
                'Cannot browser USB printers.',
                isOnlineHWagent,
            )
        }
    }, [browseUsbDevicesRequest, isOnlineHWagent])

    return {
        print: onPrint,
        openDrawer,
        checkStatusRequest: checkStatus,
        getPrinterStatusRequest,
        browseUsbDevices,
    }
}

export default useCurrentTerminalDevicesHwAgentCaller
